import { onMounted, onUnmounted } from 'vue'

import { DeferPriority } from './deferPriority'

/**
 * Options for deferred mounted hook
 */
export interface OnDeferredMountedOptions {
  /**
   * Priority level for execution
   * @default DeferPriority.NORMAL
   */
  priority?: DeferPriority

  /**
   * Maximum time to wait before forcing execution (ms)
   * If not provided, uses priority-based timeout
   */
  timeout?: number

  /**
   * Wait for initial page load to complete before executing
   * This ensures critical rendering is done first
   * @default true
   */
  waitForLoad?: boolean

  /**
   * Abort signal for cancellation
   */
  signal?: AbortSignal
}

/**
 * Maps priority to idle deadline timeout
 */
const getPriorityTimeout = (priority: DeferPriority): number => {
  switch (priority) {
    case DeferPriority.HIGH:
      return 100 // Execute within 100ms
    case DeferPriority.NORMAL:
      return 500 // Execute within 500ms
    case DeferPriority.LOW:
      return 2000 // Execute within 2 seconds
    default:
      return 500
  }
}

/**
 * Check if page has finished initial load and hydration
 */
const isPageLoaded = (): boolean => {
  if (!process.client) return false
  return document.readyState === 'complete'
}

/**
 * Safe requestAnimationFrame with fallback for older browsers
 * Falls back to setTimeout for browsers without requestAnimationFrame support (IE9 and below)
 *
 * Browser support:
 * - requestAnimationFrame: IE10+, Chrome 10+, Firefox 4+, Safari 6+
 * - Fallback (setTimeout): All browsers including IE9 and below
 */
const safeRequestAnimationFrame = (callback: () => void): number => {
  if (typeof requestAnimationFrame !== 'undefined') {
    return requestAnimationFrame(callback)
  }
  // Fallback: use setTimeout with ~16ms (60fps) delay for older browsers
  // This provides similar behavior to requestAnimationFrame
  return setTimeout(callback, 16) as unknown as number
}

/**
 * Wait for page load and initial hydration to complete
 * This ensures critical rendering is done before executing deferred work
 */
const waitForPageLoad = (): Promise<void> => {
  return new Promise((resolve) => {
    if (isPageLoaded()) {
      // Wait a bit more for hydration to complete
      // Use requestAnimationFrame (or fallback) to ensure DOM is ready
      // Double-RAF ensures we wait for next paint cycle
      safeRequestAnimationFrame(() => {
        safeRequestAnimationFrame(() => {
          resolve()
        })
      })
      return
    }

    const handler = () => {
      if (isPageLoaded()) {
        window.removeEventListener('load', handler)
        // Wait for hydration after load event
        safeRequestAnimationFrame(() => {
          safeRequestAnimationFrame(() => {
            resolve()
          })
        })
      }
    }

    window.addEventListener('load', handler)
  })
}

/**
 * Execute callback using requestIdleCallback with fallback
 */
const executeWithIdleCallback = (
  callback: () => void | Promise<void>,
  timeout: number,
  priority: DeferPriority,
  signal?: AbortSignal
): (() => void) => {
  if (!process.client) {
    // Execute immediately on server (though this shouldn't be called on server)
    void Promise.resolve(callback())
    return () => {}
  }

  let cancelled = false
  let callbackId:
    | ReturnType<typeof window.requestIdleCallback>
    | ReturnType<typeof setTimeout>
    | null = null

  const cleanup = () => {
    cancelled = true
    if (callbackId !== null) {
      if (typeof window.cancelIdleCallback !== 'undefined' && typeof callbackId === 'number') {
        // Modern browsers with cancelIdleCallback support
        window.cancelIdleCallback(callbackId as number)
      } else {
        // Fallback: use clearTimeout for setTimeout-based callbacks
        clearTimeout(callbackId as ReturnType<typeof setTimeout>)
      }
      callbackId = null
    }
  }

  // Handle abort signal
  if (signal) {
    if (signal.aborted) {
      return cleanup
    }
    signal.addEventListener('abort', cleanup)
  }

  if (typeof window.requestIdleCallback !== 'undefined') {
    // Modern browsers with requestIdleCallback support
    callbackId = window.requestIdleCallback(
      async (deadline) => {
        if (cancelled || signal?.aborted) {
          return
        }

        // Execute if we have enough time or timeout occurred
        if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
          try {
            await callback()
          } catch (error) {
            console.error('[onDeferredMounted] Error executing callback:', error)
          }
        }
      },
      { timeout }
    )
  } else {
    // Fallback for browsers without requestIdleCallback (IE, older Safari, etc.)
    // Use setTimeout with a reasonable delay to simulate idle callback
    // For HIGH priority, use shorter delay; for LOW, use longer delay
    const delay =
      priority === DeferPriority.HIGH
        ? Math.min(timeout, 50) // Execute sooner for high priority
        : priority === DeferPriority.LOW
          ? Math.min(timeout, 100) // Slightly longer for low priority
          : Math.min(timeout, 75) // Medium delay for normal priority

    callbackId = setTimeout(async () => {
      if (cancelled || signal?.aborted) {
        return
      }

      try {
        await callback()
      } catch (error) {
        console.error('[onDeferredMounted] Error executing callback:', error)
      }
    }, delay)
  }

  return cleanup
}

/**
 * Lifecycle hook that executes code after mount, only on client side,
 * and only after first load, using deferred execution to avoid blocking main thread.
 *
 * This hook is perfect for:
 * - Initializing non-critical features
 * - Loading analytics or tracking scripts
 * - Setting up non-essential event listeners
 * - Prefetching data for future interactions
 * - Any work that can wait until after initial render
 *
 * Browser Compatibility:
 * - Modern browsers: Uses requestIdleCallback and requestAnimationFrame for optimal performance
 * - Older browsers (IE9+, Safari <6): Falls back to setTimeout with appropriate delays
 * - All features work across all supported browsers with graceful degradation
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { onDeferredMounted } from '@common/core-layer/composables/onDeferredMounted'
 * import { DeferPriority } from '@common/core-layer/composables/deferPriority'
 *
 * // Basic usage - runs after mount and page load, during idle time
 * onDeferredMounted(() => {
 *   initializeAnalytics()
 *   setupNonCriticalListeners()
 * })
 *
 * // With priority - execute sooner
 * onDeferredMounted(() => {
 *   prefetchNextPageData()
 * }, {
 *   priority: DeferPriority.HIGH
 * })
 *
 * // Skip waiting for page load (execute immediately after mount)
 * onDeferredMounted(() => {
 *   initializeFeature()
 * }, {
 *   waitForLoad: false,
 *   priority: DeferPriority.NORMAL
 * })
 *
 * // With cancellation support
 * const abortController = new AbortController()
 * onDeferredMounted(() => {
 *   loadHeavyData()
 * }, {
 *   signal: abortController.signal
 * })
 *
 * // Cancel if needed
 * // abortController.abort()
 * </script>
 * ```
 *
 * @param callback - Function to execute (can be async)
 * @param options - Configuration options
 * @returns Cleanup function to cancel execution
 */
export const onDeferredMounted = (
  callback: () => void | Promise<void>,
  options: OnDeferredMountedOptions = {}
): (() => void) => {
  const { priority = DeferPriority.NORMAL, timeout, waitForLoad = true, signal } = options

  let cleanup: (() => void) | null = null

  onMounted(async () => {
    // Only run on client side
    if (!process.client) {
      return
    }

    // Wait for page load if requested
    if (waitForLoad && !isPageLoaded()) {
      await waitForPageLoad()
    }

    // Check if aborted during wait
    if (signal?.aborted) {
      return
    }

    // Calculate timeout
    const timeoutMs = timeout ?? getPriorityTimeout(priority)

    // Execute with deferred callback
    cleanup = executeWithIdleCallback(callback, timeoutMs, priority, signal)
  })

  // Cleanup on unmount
  onUnmounted(() => {
    if (cleanup) {
      cleanup()
      cleanup = null
    }
  })

  // Return cleanup function for manual cancellation
  return () => {
    if (cleanup) {
      cleanup()
      cleanup = null
    }
  }
}
